/* v3.99d.341ad — CannaForge Web Push client helpers.
 *
 * Public API:
 *   - registerServiceWorker()          — safe no-op if unsupported.
 *   - getPushPermissionState()         — 'granted' | 'denied' |
 *                                          'default' | 'unsupported'
 *   - isIosStandaloneRequired()        — iOS Safari NOT installed
 *                                          as PWA → cannot subscribe.
 *   - subscribeToPush()                — full flow: request perm,
 *                                          subscribe via PushManager,
 *                                          POST to backend.
 *   - unsubscribeFromPush()            — clean removal.
 *
 * All functions fail safe — they never throw into the UI layer.
 */
import { api } from "./apiClient";

function urlB64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return typeof window !== "undefined"
      && "serviceWorker" in navigator
      && "PushManager" in window
      && "Notification" in window;
}

export function getPushPermissionState() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission;   // 'granted' | 'denied' | 'default'
}

// iPhone/iPad Safari can only subscribe to push when the site is
// installed as a Home Screen PWA (standalone). Detect the case where
// we need to show the "Add to Home Screen" instructions.
export function isIosStandaloneRequired() {
  const ua = navigator.userAgent || "";
  const isIos = /iPhone|iPad|iPod/i.test(ua);
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches
                   || window.navigator.standalone === true;
  return isIos && !standalone;
}

export async function registerServiceWorker() {
  if (!isPushSupported()) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js");
  } catch (e) {
    console.warn("[push] sw register failed:", e);
    return null;
  }
}

async function fetchVapidKey() {
  const { data } = await api.get("/push/vapid-key");
  return data?.public_key || "";
}

export async function subscribeToPush() {
  if (!isPushSupported()) {
    return { ok: false, reason: "unsupported" };
  }
  if (isIosStandaloneRequired()) {
    return { ok: false, reason: "ios_needs_home_screen" };
  }
  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    return { ok: false, reason: perm };  // 'denied' | 'default'
  }
  const reg = (await navigator.serviceWorker.getRegistration())
            || await registerServiceWorker();
  if (!reg) return { ok: false, reason: "sw_unavailable" };
  const vapid = await fetchVapidKey();
  if (!vapid) return { ok: false, reason: "vapid_missing" };
  let sub;
  try {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(vapid),
    });
  } catch (e) {
    console.warn("[push] subscribe failed:", e);
    return { ok: false, reason: "pushmanager_failed",
             error: String(e) };
  }
  const json = sub.toJSON();
  try {
    await api.post("/push/subscribe", {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      user_agent: navigator.userAgent,
      platform: navigator.platform || null,
    });
  } catch (e) {
    console.warn("[push] backend save failed:", e);
    return { ok: false, reason: "backend_save_failed" };
  }
  return { ok: true };
}

export async function unsubscribeFromPush() {
  if (!isPushSupported()) return { ok: false };
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return { ok: false };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch (_) {}
  try {
    await api.delete("/push/subscribe", { data: { endpoint } });
  } catch (_) {}
  return { ok: true };
}

export async function sendTestPush() {
  try {
    const { data } = await api.post("/push/test", {});
    return data;
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}
